import { useState } from "react";
import { useDebounce } from "react-use";
import { Button } from "@/shared/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/shared/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { LayoutTemplateIcon, FilterIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { templateBuilderQueries } from "@/features/template-builder/queries";
import { TemplateCard } from "./components/template-card";
import { NewTemplateCard } from "./components/template-card-new";
import { CreateTemplateDialog } from "./components/create-template-dialog";
import { FilterBar } from "@/shared/components/filter-bar";
import { useTenant } from "@/domains/tenant/hooks";
import { useRecordPermissions } from "@/domains/tenant/hooks";
import { ContactSalesButton } from "@/domains/billing/components";

export function TemplateOverviewPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showGlobal, setShowGlobal] = useState(true);
  const { data: tenantData } = useTenant();
  const { canEdit } = useRecordPermissions();

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    300,
    [search],
  );

  const { data, isLoading } = useQuery(
    templateBuilderQueries.list(
      { search: debouncedSearch, showGlobal },
      tenantData?.tenant.tenant_id,
    ),
  );
  const orderedTemplates = [
    ...(data ?? []).filter((item) => !item.isGlobal),
    ...(data ?? []).filter((item) => item.isGlobal),
  ];
  const isPublicTenant = tenantData?.tenant?.tenant_name === "Public";

  return (
    <>
      <title>Template Builder - Researchly</title>
      <div className="flex flex-col gap-6 p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <LayoutTemplateIcon className="size-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Template Builder
            </h1>
            <p className="text-sm text-muted-foreground">
              Create and manage reusable analysis templates
            </p>
          </div>
        </div>

        {/* Search */}
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search templates..."
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-1.5">
                <FilterIcon className="size-4" />
                Filters
                {!showGlobal && (
                  <span className="ml-1 text-xs text-muted-foreground">
                    (1)
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Scope</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuCheckboxItem
                  checked={showGlobal}
                  onCheckedChange={(checked) => setShowGlobal(checked === true)}
                  onSelect={(event) => event.preventDefault()}
                >
                  Global templates
                </DropdownMenuCheckboxItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </FilterBar>

        {/* Grid */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {/* New Template Card - always first */}
          {isPublicTenant ? (
            <div className="relative">
              <div className="pointer-events-none select-none blur-[1.5px] opacity-60">
                <NewTemplateCard />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <ContactSalesButton />
              </div>
            </div>
          ) : (
            <CreateTemplateDialog trigger={<NewTemplateCard />} />
          )}

          {isLoading && (
            <Card className="sm:col-span-1 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Loading templates…</CardTitle>
                <CardDescription>
                  Fetching your analysis templates.
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          {!isLoading &&
            orderedTemplates.map((item) => (
              <TemplateCard
                key={item.id}
                item={item}
                isReadOnly={!canEdit(item.recordTenantId)}
              />
            ))}

          {!isLoading && orderedTemplates.length === 0 && !debouncedSearch && (
            <Card className="sm:col-span-1 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">No templates yet</CardTitle>
                <CardDescription>
                  Create your first template to get started with reusable
                  analysis workflows.
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          {!isLoading && orderedTemplates.length === 0 && debouncedSearch && (
            <Card className="sm:col-span-1 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">No templates found</CardTitle>
                <CardDescription>
                  Try adjusting your search term.
                </CardDescription>
              </CardHeader>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
